/**
 * CoachAvailabilityEditor — weekly availability editor shared by the coach
 * self-portal and the facility operator's Coach detail.
 *
 * With no props it is caller-scoped and uses the authenticated coach's uid.
 * Operator mode receives an explicit coach + facility: it edits only that
 * facility's visible slots while retaining every other slot in the replace-all
 * payload. The backend authorizes both self and facility-manager mutations.
 *
 * THIN presentation only — the backend is the authority:
 *   - current availability is read from the club profile + facility endpoint
 *     and grouped by day of week (0=Mon … 6=Sun — NOT JS getDay() 0=Sun);
 *   - the editor holds the FULL set in memory; add a slot (day + start + end),
 *     remove a slot. A thin VIEW guard (start < end, valid HH:MM) blocks an
 *     invalid add so we never PUT a known-bad slot — the backend remains the
 *     authority and a 422 still surfaces as an honest notification;
 *   - Save → setAvailability(me, allSlots) (PUT replace-all, an emptied set sends
 *     []) → RE-LIST from the server. NO optimistic: the displayed slots reflect
 *     the server result only AFTER the save resolves;
 *   - a 4xx/403 on save → honest error notification, NO re-list, the on-screen set
 *     unchanged (no phantom save); Save is disabled in-flight (re-entrancy guard,
 *     mirroring the inbox busyRef + load() pattern).
 *
 * ARMED-ONLY-WHEN-HYDRATED (replace-all safety): because Save PUTs the WHOLE
 * in-memory set, an empty/stale set must never be PUT-able. The editor is not
 * "armed" until the CURRENT uid's availability has successfully hydrated:
 *   - Save is disabled (and its handler inert) while loading, after a load
 *     failure, and with no uid — a click there would replace-all with the
 *     never-loaded [] and silently WIPE the coach's real availability;
 *   - a failed load shows the role=alert with a Retry — never an armed Save;
 *   - an Add attempted before hydration is rejected with on-screen feedback
 *     (role=alert), never silently absorbed (a draft accepted mid-load would be
 *     wiped by setSlots when the GET resolves).
 *
 * FROZEN-WHILE-SAVING: the PUT body is a snapshot of the set at click time. Any
 * edit accepted during the in-flight PUT would be silently reverted by the
 * post-save re-list — so the whole editor freezes during a save (add form +
 * Remove + Save all disabled), same philosophy as the inbox row-freeze.
 *
 * STALE-RESPONSE / LIVENESS GUARDS: load() carries a sequence + uid identity
 * guard (the IncomeCard `active`-flag pattern) — a late GET for a PREVIOUS uid
 * or a superseded load is discarded, and an unmounted instance never re-lists
 * or toasts (no "Availability saved." over a view that can't show it).
 *
 * UUIDs (slot_id, facility_id) are NEVER rendered as visible text — a window is
 * identified by its weekday + times only.
 *
 * OPTIMISTIC CONCURRENCY (#1386): the replace-all PUT now carries a precondition.
 * On each successful load we capture a snapshot token = the MAX `updated_at`
 * across the loaded slots (the version the user is editing against) and send it
 * back on Save as `If-Unmodified-Since`. If another session saved in between, the
 * backend rejects with a structured 412 (`AVAILABILITY_CONFLICT`); we do NOT
 * treat that as a generic failure — we REFETCH the latest and show a clear,
 * non-destructive warning so the stale write is abandoned, never silently
 * applied. An empty initial load (no slots) sends no token → the first write
 * proceeds. (The bare-token fix lives in the backend; this is the FE adoption.)
 */
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import ScheduleIcon from "@mui/icons-material/ScheduleOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import NativeSelect from "@mui/material/NativeSelect";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { isApiError } from "@bw/shared/api";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { notifications } from "@/app/notifications";
import {
  getAvailability,
  setAvailability,
  type CoachAvailabilitySlot,
  type CoachAvailabilitySlotInput,
} from "@/libs/coachingApi";

// day_of_week is 0=Mon … 6=Sun (per common/models.py CoachAvailabilitySlotCreate
// `Field(..., ge=0, le=6, description="0=Mon, 6=Sun")`). This is NOT the JS
// getDay() 0=Sun convention — a day_of_week=0 slot belongs to MONDAY. The index
// of each name in this array IS its day_of_week, so iterating it renders the week
// Monday → Sunday in order.
const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

// Short forms used as the Day option's TEXT CONTENT. A full weekday word as
// option text would collide with the same word used as the weekly-grid HEADING
// (getByText(/monday/i) would match both). Each option carries the full
// weekday name in BOTH its `value` attribute (selectOptions(control,
// "Wednesday") matches by value) AND its HTML `label` attribute — per the HTML
// spec + HTML-AAM, browsers DISPLAY the label attribute ("Wednesday") instead
// of the text content and expose it as the option's ACCESSIBLE NAME, so real
// users (sighted and AT) get the full weekday. NOT aria-label: every weekday
// name contains "day", so aria-label would make getByLabelText(/day/i) match
// all 7 options instead of only the labelled select. Note the jsdom accname
// polyfill (dom-accessibility-api) does not implement HTML-AAM's option-label
// step, so tests pin this contract at the attribute level, not by role name.
const WEEKDAYS_SHORT = [
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun",
] as const;

// A draft window held in memory. id is a client-only stable key for list
// rendering + removal (NOT sent to the server, NOT rendered as text). Existing
// slots carry their facility_id through so a replace-all PUT preserves it; an
// add defaults to null (facility-agnostic).
interface DraftSlot {
  id: string;
  facility_id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
}

let draftSeq = 0;
function nextDraftId(): string {
  draftSeq += 1;
  return `draft-${draftSeq}`;
}

function toDraft(slot: CoachAvailabilitySlot): DraftSlot {
  return {
    id: nextDraftId(),
    facility_id: slot.facility_id,
    day_of_week: slot.day_of_week,
    start_time: slot.start_time,
    end_time: slot.end_time,
  };
}

// Strict HH:MM (24h) — the thin view guard. The backend remains the authority.
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

function isValidTime(value: string): boolean {
  return HHMM.test(value);
}

// The optimistic-concurrency precondition token (#1386): the MAX `updated_at`
// across the just-loaded slots — the snapshot the user is editing against. Sent
// back on Save as `If-Unmodified-Since`; the backend rejects (412) if its stored
// state is newer. An EMPTY set has no version to guard (nothing saved to clobber)
// → null, so a first write proceeds with no header. Compared as ISO-8601 strings,
// which sort lexicographically iff zero-padded + same offset (the server emits a
// uniform "...Z"); we forward the raw winner verbatim so the backend parses the
// exact value it issued rather than a re-serialized one.
function maxUpdatedAt(slots: CoachAvailabilitySlot[]): string | null {
  let max: string | null = null;
  for (const s of slots) {
    if (max === null || s.updated_at > max) max = s.updated_at;
  }
  return max;
}

interface CoachAvailabilityEditorProps {
  coachProfileId: string;
  facilityId: string;
}

export function CoachAvailabilityEditor({
  coachProfileId,
  facilityId,
}: CoachAvailabilityEditorProps) {
  const editorIdentity = `${coachProfileId}\u0000${facilityId}`;

  // Per-instance unique control ids — two mounted editors (or a remount racing
  // an unmount) must not collide on label→control wiring.
  const idPrefix = useId();

  const [slots, setSlots] = useState<DraftSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // True ONLY between a successfully APPLIED load for the current uid and the
  // start of the next load. While false the editor is NOT ARMED: Save must not
  // PUT (the in-memory set is [] or stale — a replace-all would wipe real data)
  // and an Add is rejected with feedback (it would be wiped by the load apply).
  const [hydratedIdentity, setHydratedIdentity] = useState<string | null>(null);

  // Add-slot form state.
  const [formDay, setFormDay] = useState<number>(0);
  const [formStart, setFormStart] = useState("");
  const [formEnd, setFormEnd] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  // Save re-entrancy guard — the in-flight Save is disabled so a double-click
  // fires setAvailability only once (mirrors the inbox busyRef pattern).
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  // DURABLE save feedback (NOT notifications-only). A rejected Save sets this true,
  // rendering an INLINE role="alert" inside the editor AND marking the on-screen
  // set "Unsaved" — so it can never keep LOOKING saved (the live data-loss
  // illusion: the PUT 404'd, the notifications was blank over HTTP/2, and the slot stayed
  // on screen as if persisted). Inline survives any notifications problem. Cleared the
  // moment another save starts (a fresh attempt is in flight again).
  const [saveError, setSaveError] = useState(false);
  // Concurrency-conflict feedback (#1386). A 412 AVAILABILITY_CONFLICT is NOT a
  // generic save failure — someone else saved this coach's availability since we
  // loaded it. We refetch the latest (so the editor shows server truth) and set
  // this flag to render a distinct, non-destructive role="alert": the stale write
  // was abandoned, not applied. Cleared when another save starts.
  const [conflict, setConflict] = useState(false);
  // The optimistic-concurrency precondition token: max(updated_at) captured at the
  // LAST applied load — the snapshot the user is editing against. A ref (not state)
  // because Save reads it at click time and it must never trigger a re-render. null
  // when the loaded set is empty (no version to guard → first write needs no header).
  const versionRef = useRef<string | null>(null);

  // Liveness: an unmounted instance must not apply state, re-list, or notifications.
  const aliveRef = useRef(true);
  // Identity: each load() takes a ticket; only the LATEST ticket may apply its
  // response. A late GET for a previous uid (in-place auth swap) or a superseded
  // load is discarded — coach B must never see (or be able to Save) coach A's
  // windows.
  const loadTicketRef = useRef(0);
  const identityRef = useRef(editorIdentity);
  identityRef.current = editorIdentity;

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    setFormDay(0);
    setFormStart("");
    setFormEnd("");
    setFormError(null);
    setSaveError(false);
    setConflict(false);
  }, [editorIdentity]);

  const load = useCallback(async () => {
    const identity = editorIdentity;
    // A save started under a previous facility may call its captured load()
    // after the operator switches facilities. It must not supersede the new
    // facility's load ticket.
    if (identity !== identityRef.current) return;
    const ticket = ++loadTicketRef.current;
    // Any newer load supersedes in-flight ones; de-arm until this one applies.
    setHydratedIdentity(null);
    if (!coachProfileId || !facilityId) {
      // Missing identity scope: nothing is requested and the replace-all
      // editor stays disarmed until self identity or operator facility resolves.
      setLoading(false);
      setLoadError(null);
      setSlots([]);
      versionRef.current = null;
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      // Caller-scoped: the CALLER's own uid, never another id.
      const result = await getAvailability(coachProfileId, facilityId);
      if (
        !aliveRef.current ||
        ticket !== loadTicketRef.current ||
        identity !== identityRef.current
      )
        return;
      setSlots(result.map(toDraft));
      // Capture the precondition token from the freshly-loaded server set — the
      // snapshot Save will guard against. null for an empty set (#1386).
      versionRef.current = maxUpdatedAt(result);
      setHydratedIdentity(identity);
    } catch (err) {
      if (
        !aliveRef.current ||
        ticket !== loadTicketRef.current ||
        identity !== identityRef.current
      )
        return;
      setLoadError(
        err instanceof Error ? err.message : "Failed to load availability",
      );
    } finally {
      if (
        aliveRef.current &&
        ticket === loadTicketRef.current &&
        identity === identityRef.current
      )
        setLoading(false);
    }
  }, [coachProfileId, editorIdentity, facilityId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The editor is armed only when the CURRENT uid's set has hydrated and no
  // load/error is outstanding. While un-armed, Save is inert (a replace-all PUT
  // of a never-loaded [] is total data loss against the real backend).
  const armed =
    Boolean(coachProfileId) && Boolean(facilityId) &&
    hydratedIdentity === editorIdentity &&
    !loading &&
    !loadError;

  const handleAdd = useCallback(() => {
    if (saving) return; // frozen during an in-flight save (controls disabled too)
    if (!armed) {
      // Honest rejection, never a silent absorb: a draft accepted mid-load
      // would be wiped by setSlots(result) when the GET resolves.
      setFormError(
        "Availability isn’t loaded yet — wait for it to load (or retry) before adding times.",
      );
      return;
    }
    // Thin VIEW guard — never PUT a known-bad slot. The backend stays authority.
    if (!isValidTime(formStart) || !isValidTime(formEnd)) {
      setFormError("Enter a valid start and end time in HH:MM format.");
      return;
    }
    if (formEnd <= formStart) {
      setFormError("End time must be after start time.");
      return;
    }
    setFormError(null);
    setSlots((prev) => [
      ...prev,
      {
        id: nextDraftId(),
        facility_id: facilityId,
        day_of_week: formDay,
        start_time: formStart,
        end_time: formEnd,
      },
    ]);
    setFormStart("");
    setFormEnd("");
  }, [armed, saving, facilityId, formDay, formStart, formEnd]);

  const handleRemove = useCallback(
    (id: string) => {
      if (saving) return; // frozen during an in-flight save (buttons disabled too)
      setSlots((prev) => prev.filter((s) => s.id !== id));
    },
    [saving],
  );

  const handleSave = useCallback(async () => {
    if (savingRef.current) return; // re-entrancy guard
    if (!armed) return; // not hydrated → a PUT here would wipe real data
    savingRef.current = true;
    const saveIdentity = editorIdentity;
    setSaving(true);
    setSaveError(false); // a fresh attempt is in flight — clear stale failure
    setConflict(false); // …and any prior concurrency-conflict warning
    try {
      const body: CoachAvailabilitySlotInput[] = slots.map((s) => ({
        facility_id: s.facility_id,
        day_of_week: s.day_of_week,
        start_time: s.start_time,
        end_time: s.end_time,
      }));
      // Optimistic-concurrency precondition (#1386): echo the snapshot token from
      // our last load so the backend rejects (412) if someone saved in between,
      // instead of letting this replace-all silently clobber the newer state.
      // null token (empty load) → no header → first write proceeds.
      await setAvailability(coachProfileId, body, versionRef.current ?? undefined);
      // Liveness: if this instance unmounted while the PUT was in flight, do
      // NOT re-list and do NOT claim success — a fresh instance owns the view
      // now, and a global "saved" notifications over a view that can't show the result
      // would be a lie.
      if (!aliveRef.current || saveIdentity !== identityRef.current) return;
      // Success → RE-LIST from the server (NO optimistic flip; the displayed set
      // reflects the server result only after this refetch confirms it).
      await load();
      if (!aliveRef.current || saveIdentity !== identityRef.current) return;
      notifications.success("Availability saved.");
    } catch (err) {
      if (!aliveRef.current || saveIdentity !== identityRef.current) return;
      // A 412 AVAILABILITY_CONFLICT is NOT a generic failure — another session
      // saved this coach's availability since we loaded it. The stale write was
      // REJECTED (never applied), so we abandon it: REFETCH the latest (so the
      // editor shows server truth, including the new version token) and surface a
      // distinct, non-destructive warning telling the user to review + save again.
      if (isApiError(err) && err.code === "AVAILABILITY_CONFLICT") {
        setConflict(true);
        await load(); // pull the latest set + a fresh precondition token
        if (!aliveRef.current || saveIdentity !== identityRef.current) return;
        notifications.info(
          "Availability changed elsewhere — reloaded the latest.",
        );
        return;
      }
      // Any other rejected save NEVER re-lists (which could mask the failure) —
      // the on-screen set is left unchanged. Feedback is DURABLE + INLINE (not
      // notifications-only): set the role="alert" + "Unsaved" marker so the set can
      // never keep looking saved. We also fire a notifications for parity, but the
      // inline alert is the contract — never render the raw error message
      // (it can carry a uid; statusText is "" over HTTP/2 anyway).
      setSaveError(true);
      notifications.error("Couldn’t save your availability.");
    } finally {
      savingRef.current = false;
      if (aliveRef.current) setSaving(false);
    }
  }, [armed, coachProfileId, editorIdentity, slots, load]);

  // Group the in-memory set by weekday for display. The array index IS the
  // day_of_week (0=Mon … 6=Sun), so this renders Monday → Sunday in order.
  const visibleSlots = facilityId
    ? slots.filter((slot) => slot.facility_id === facilityId)
    : slots;
  const byDay = WEEKDAYS.map((_, day) =>
    visibleSlots
      .filter((s) => s.day_of_week === day)
      .sort((a, b) => a.start_time.localeCompare(b.start_time)),
  );

  return (
    <Paper
      aria-label="Coach availability"
      component="section"
      variant="outlined"
      sx={{ p: 3 }}
    >
      <Typography component="h2" variant="h6" sx={{ mb: 2.5, fontWeight: 700 }}>
        Availability
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Times shown and saved in UTC.</Typography>
      <Stack spacing={3}>
        {!coachProfileId ? (
          // Honest signed-out state — no fake busy region, no dead controls.
          <Typography color="text.secondary" variant="body2">
            Sign in to manage availability.
          </Typography>
        ) : (
          <>
            {/* Add-slot form — labelled controls driven by accessible name.
                Interactive while loading (the handler rejects with on-screen
                feedback — see handleAdd) but FROZEN while saving. */}
            <Stack
              component="form"
              direction="row"
              onSubmit={(event) => {
                event.preventDefault();
                handleAdd();
              }}
              sx={{ alignItems: "flex-end", flexWrap: "wrap", gap: 1.5 }}
            >
              <FormControl size="small" sx={{ minWidth: 128 }}>
                {/* Native <select>. To keep each weekly-grid HEADING the SOLE
                    weekday-word TEXT match for getByText(/monday/i), the
                    option's TEXT CONTENT is the abbreviation ("Mon"). The full
                    weekday name rides on the option's `value` (selectOptions
                    matches by value) and its HTML `label` attribute — browsers
                    display + announce the label attribute ("Wednesday"), so
                    real users get the full weekday (see WEEKDAYS_SHORT note).
                    We map the chosen value back to its day index (0=Mon). */}
                <InputLabel htmlFor={`${idPrefix}-day`}>Day</InputLabel>
                <NativeSelect
                  disabled={saving}
                  inputProps={{ id: `${idPrefix}-day` }}
                  onChange={(event) =>
                    setFormDay(
                      WEEKDAYS.indexOf(
                        event.target.value as (typeof WEEKDAYS)[number],
                      ),
                    )
                  }
                  value={WEEKDAYS[formDay]}
                >
                  {WEEKDAYS.map((name, day) => (
                    <option key={name} value={name} label={name}>
                      {WEEKDAYS_SHORT[day]}
                    </option>
                  ))}
                </NativeSelect>
              </FormControl>
              <TextField
                disabled={saving}
                label="Start time"
                onChange={(event) => setFormStart(event.target.value)}
                placeholder="HH:MM"
                size="small"
                sx={{ width: 136 }}
                value={formStart}
              />
              <TextField
                disabled={saving}
                label="End time"
                onChange={(event) => setFormEnd(event.target.value)}
                placeholder="HH:MM"
                size="small"
                sx={{ width: 136 }}
                value={formEnd}
              />
              <Button
                disabled={saving}
                size="small"
                type="submit"
                variant="contained"
              >
                Add slot
              </Button>
            </Stack>
            {formError ? <Alert severity="error">{formError}</Alert> : null}

            {/* Weekly grid — grouped Monday → Sunday. */}
            {loading ? (
              <Stack
                direction="row"
                role="status"
                spacing={1.5}
                sx={{ alignItems: "center" }}
              >
                <CircularProgress size={20} />
                <Typography color="text.secondary" variant="body2">
                  Loading availability…
                </Typography>
              </Stack>
            ) : loadError ? (
              <Alert
                action={
                  <Button
                    color="inherit"
                    onClick={() => void load()}
                    size="small"
                  >
                    Retry
                  </Button>
                }
                severity="error"
              >
                Couldn’t load your availability. {loadError}
              </Alert>
            ) : (
              <Box
                component="ul"
                sx={{
                  display: "grid",
                  gap: 1.5,
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                  },
                  listStyle: "none",
                  m: 0,
                  p: 0,
                }}
              >
                {WEEKDAYS.map((name, day) => {
                  const windows = byDay[day];
                  return (
                    <Paper
                      component="li"
                      data-day={name}
                      key={name}
                      variant="outlined"
                      sx={{ p: 2 }}
                    >
                      <Typography
                        component="h3"
                        variant="subtitle2"
                        sx={{ fontWeight: 700 }}
                      >
                        {name}
                      </Typography>
                      {windows.length === 0 ? (
                        <Typography
                          color="text.secondary"
                          variant="body2"
                          sx={{ mt: 1 }}
                        >
                          No availability set
                        </Typography>
                      ) : (
                        <Stack
                          component="ul"
                          spacing={1}
                          sx={{ listStyle: "none", m: 0, mt: 1, p: 0 }}
                        >
                          {windows.map((slot) => (
                            <Paper
                              component="li"
                              key={slot.id}
                              variant="outlined"
                              sx={{ px: 1.5, py: 1 }}
                            >
                              <Stack
                                direction="row"
                                sx={{
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  sx={{ alignItems: "center" }}
                                >
                                  <ScheduleIcon
                                    color="action"
                                    fontSize="small"
                                  />
                                  <Typography variant="body2">
                                    {slot.start_time} – {slot.end_time}
                                  </Typography>
                                </Stack>
                                <Button
                                  color="error"
                                  disabled={saving}
                                  onClick={() => handleRemove(slot.id)}
                                  size="small"
                                  startIcon={<DeleteOutlineIcon />}
                                  type="button"
                                >
                                  Remove
                                </Button>
                              </Stack>
                            </Paper>
                          ))}
                        </Stack>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            )}

            {/* Concurrency-conflict warning (#1386) — a 412 AVAILABILITY_CONFLICT.
                NON-DESTRUCTIVE: the stale write was REJECTED, and we've already
                refetched the latest set (server truth is on screen above). Tell
                the user plainly so they review + save again rather than assuming
                their edit landed. Distinct from the "Unsaved" failure alert: this
                is "someone else changed it", not "the server errored". */}
            {conflict ? (
              <Alert severity="warning">
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ fontWeight: 700 }}
                >
                  Availability changed elsewhere —
                </Typography>{" "}
                <Typography component="span" variant="body2">
                  it was updated since you opened this. We’ve reloaded the
                  latest; review and save again.
                </Typography>
              </Alert>
            ) : null}

            {/* DURABLE inline save-failure feedback — NOT notifications-only. A rejected
                Save renders this role="alert" AND marks the set "Unsaved" so it
                can never keep LOOKING saved. Inline survives a missing/blank
                notifications (statusText is "" over HTTP/2 → a notifications would be invisible).
                The raw error message is NEVER rendered (it can carry a uid). */}
            {saveError ? (
              <Alert severity="error">
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ fontWeight: 700 }}
                >
                  Unsaved —
                </Typography>{" "}
                <Typography component="span" variant="body2">
                  we couldn’t save your availability. Your changes are still
                  here; try Save again.
                </Typography>
              </Alert>
            ) : null}

            <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
              {/* Armed only when hydrated: Save PUTs the WHOLE set (replace-all),
                  so it must be inert while the set is un-loaded ([]) or stale —
                  one click there would wipe the coach's real availability. */}
              <Button
                disabled={saving || !armed}
                onClick={() => void handleSave()}
                size="small"
                type="button"
                variant="contained"
              >
                Save
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  );
}
